"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getUserStatus, getUserTermsAccepted } from "../services/UserServices"
import { useAuth } from "../contexts/authContext"
import "../assets/styles/UserStatusCheck.css"

const UserStatusCheck = ({ children }) => {
  const [isActive, setIsActive] = useState(null)
  const [termsAccepted, setTermsAccepted] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { getToken, currentUser, loading: authLoading } = useAuth()

  // Verificamos si es admin usando la misma logica que en SellerRoute
  const isAdmin = currentUser && currentUser.photoURL === "admin"

  useEffect(() => {
    if (authLoading) return

    const checkUserStatus = async () => {
      const runWithAuthRetry = async (requestFn, token) => {
        try {
          return { value: await requestFn(token), token }
        } catch (error) {
          if (error?.status !== 401) {
            throw error
          }

          const refreshedToken = await getToken()
          if (!refreshedToken) {
            throw error
          }

          return { value: await requestFn(refreshedToken), token: refreshedToken }
        }
      }

      try {
        // Si es admin, permitimos acceso sin verificar estado
        if (isAdmin) {
          setIsActive(true) // Para administradores, consideramos que siempre estan activos
          setTermsAccepted(true) // Para administradores, consideramos que siempre han aceptado los terminos
          setLoading(false)
          return // Salimos temprano si es admin
        }

        let token = await getToken()
        if (!token) {
          throw new Error("No se encontro token de autenticacion")
        }

        // Verificar si el usuario esta activo
        const statusResult = await runWithAuthRetry(getUserStatus, token)
        token = statusResult.token
        const status = statusResult.value
        setIsActive(status)

        // Si el usuario no esta activo, redirigir a /notapproved
        if (!status) {
          navigate("/notapproved")
          return
        }

        // Si el usuario esta activo, verificar si ha aceptado los terminos
        const termsResult = await runWithAuthRetry(getUserTermsAccepted, token)
        const terms = termsResult.value
        setTermsAccepted(terms)

        // Si el usuario no ha aceptado los terminos, redirigir a /acceptterms
        if (!terms) {
          navigate("/acceptterms")
        }
      } catch (error) {
        console.error("Error checking user status:", error)
        navigate("/signin")
      } finally {
        setLoading(false)
      }
    }

    checkUserStatus()
  }, [authLoading, getToken, navigate, isAdmin, currentUser])

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Cargando, por favor espere...</p>
      </div>
    )
  }

  // Permitimos el acceso si el usuario está activo Y ha aceptado los términos, O si es administrador
  return (isActive && termsAccepted) || isAdmin ? children : null
}

export default UserStatusCheck
